// utils/pdf.js
import { jsPDF } from "jspdf";


// Sizes in mm (tweak to taste)
/*const CREST_D   = 56;  // crest diameter
const CREST_PAD = 6;   // inner padding for logo
const CREST_GAP = 10;  // space below crest before headline

async function drawHeroBrand(doc, { pageW, y, logoUrl, venueName, accentHex }) {
  const cx = pageW / 2;
  const r  = CREST_D / 2;
  //const x  = cx - r;
  const rgb = hexToRgb(accentHex);

  // 1) Crest plate (white circle + subtle ring)
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(230, 230, 230);
  doc.setLineWidth(0.3);
  doc.circle(cx, y + r, r, 'FD'); // F=fill, D=stroke

  let drewSomething = false;

  // 2) Try to draw the logo inside the crest
  if (logoUrl) {
    const { dataURL, w, h } = await imageUrlToDataURLWithSize(logoUrl);
    if (dataURL && w && h) {
      const innerW = CREST_D - CREST_PAD * 2;
      const innerH = CREST_D - CREST_PAD * 2;
      // NOTE: jsPDF units are mm; 'w'/'h' from <img> are px.
      // jsPDF will accept mm sizes directly; we just preserve aspect ratio.
      const scale = Math.min(innerW / w, innerH / h, 1);
      const drawW = w * scale;
      const drawH = h * scale;
      const imgX  = cx - drawW / 2;
      const imgY  = y + r - drawH / 2;

      doc.addImage(dataURL, 'PNG', imgX, imgY, drawW, drawH, undefined, 'FAST');
      drewSomething = true;
    }
  }

  // 3) Fallback: venue initials inside the crest (if no logo or load failed)
  if (!drewSomething) {
    const initials = makeInitials(venueName);
    doc.setTextColor(rgb.r, rgb.g, rgb.b);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20); // looks balanced inside a 56mm crest
    doc.text(initials || '•', cx, y + r + 7, { align: 'center' });
  }

  return y + CREST_D + CREST_GAP; // advance by fixed height either way
}

// Make "Casa Del Darah" -> "CD"
function makeInitials(name = '') {
  const words = name
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .filter(Boolean);
  if (!words.length) return '';
  const first = words[0][0] || '';
  const second = (words[1] && words[1][0]) || '';
  return (first + second).toUpperCase();
}

// Convert #RRGGBB to {r,g,b}
function hexToRgb(hex = '#000000') {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m
    ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) }
    : { r: 0, g: 0, b: 0 };
}

// Load image -> dataURL + intrinsic size (browser-side)
function imageUrlToDataURLWithSize(url) {
  return new Promise((resolve) => {
    if (!url) return resolve({ dataURL: null, w: 0, h: 0 });
    const img = new Image();
    img.crossOrigin = 'anonymous'; // the image host must allow CORS
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve({
        dataURL: canvas.toDataURL('image/png'),
        w: img.naturalWidth,
        h: img.naturalHeight,
      });
    };
    img.onerror = () => resolve({ dataURL: null, w: 0, h: 0 });
    img.src = url;
  });
}*/


/**
 * Generate a simple A4 flyer with venue name, optional logo, and your existing QR dataURL.
 * - variant: "outside" (marketing) or "table" (utility)
 * - qrDataURL: pass your <img :src="qrcodeString"> value directly
 */
export async function generateFlyerPDF({
  venueName,
//  logoUrl,        // optional: remote logo URL (PNG/JPG)
  qrDataURL,      // REQUIRED for placing the QR (data:image/png;base64,...)
  descriptionText,
  accentHex = "#83548C",
  fileName = "flyer.pdf",
}) {
 const doc = new jsPDF({ unit: "mm", format: "a4" });
const pageW = doc.internal.pageSize.getWidth();
const pageH = doc.internal.pageSize.getHeight();

const margin  = 15;
const headerH = 38;  // bigger header
const footerH = 16;

// --- Header band ---
doc.setFillColor(accentHex);
doc.rect(0, 0, pageW, headerH, "F");

// Venue name (centered, auto-fit)
const title = venueName || "Venue Name";
doc.setTextColor(255, 255, 255);
doc.setFont("helvetica", "bold");

const maxTextW = pageW - margin * 2;
let fontSize = 40;
doc.setFontSize(fontSize);
while (doc.getTextWidth(title) > maxTextW && fontSize > 12) {
  fontSize -= 1;
  doc.setFontSize(fontSize);
}
doc.text(title, pageW / 2, headerH / 2, { align: "center", baseline: "middle" });

// --- Footer (unchanged, left-aligned) ---
doc.setFillColor(accentHex);
doc.rect(0, pageH - footerH, pageW, footerH, "F");
doc.setTextColor(255, 255, 255);
doc.setFont("helvetica", "normal");
doc.setFontSize(11);
doc.text("Powered by VibeTap", margin, pageH - 6);

// --- Body layout rhythm ---
const GAP_AFTER_HEADER   = 16;
const GAP_HEADLINE_TO_SUB = 6;
const GAP_SUB_TO_QR       = 18;
const GAP_QR_TO_CTA       = 22;

let y = headerH + GAP_AFTER_HEADER;

// Headline
doc.setTextColor(20, 20, 20);
doc.setFont("helvetica", "bold");
doc.setFontSize(36);
doc.text("Skip the Line. Order Now.", pageW / 2, y, { align: "center" });

y += GAP_HEADLINE_TO_SUB;

// Subhead (softer)
doc.setFont("helvetica", "normal");
doc.setFontSize(13);
doc.setTextColor(85, 90, 100);
doc.text(
  "Scan the QR to get started before you even walk in.",
  pageW / 2,
  y,
  { align: "center", maxWidth: pageW - margin * 2 }
);

// Reset main text color
doc.setTextColor(20, 20, 20);

y += GAP_SUB_TO_QR;

// --- QR placement ---
const qrSize = 90;
const qrX = (pageW - qrSize) / 2;
const qrY = y;

// Accent ring
doc.setDrawColor(accentHex);
doc.setLineWidth(1.5);
doc.ellipse(pageW / 2, qrY + qrSize / 2, qrSize / 2 + 6, qrSize / 2 + 6);

// Actual QR image (from your dataURL)
if (qrDataURL) {
  doc.addImage(qrDataURL, "PNG", qrX, qrY, qrSize, qrSize, undefined, "FAST");
} else {
  // fallback placeholder
  doc.setDrawColor(200);
  doc.rect(qrX, qrY, qrSize, qrSize);
  doc.setFontSize(11);
  doc.setTextColor(120);
  doc.text("QR code goes here", pageW / 2, qrY + qrSize / 2, { align: "center" });
  doc.setTextColor(20, 20, 20);
}

// --- CTA line ---
const horizontalMargin = 30;
const maxTextWidth = pageW - horizontalMargin * 2;

doc.setFont("helvetica", "bold");
doc.setFontSize(20);
doc.text(
  descriptionText,
  pageW / 2,
  qrY + qrSize + GAP_QR_TO_CTA,
  { align: "center", maxWidth: maxTextWidth }
);

// Done
doc.save(fileName);

}

/**
 * Generate PDFs for table QR codes.
 * layouts:
 *  - "two-up": 2 per page (stacked), large QR, reduced whitespace
 *  - "grid":   2x3 per page (small tent/handout)
 *  - "single": 1 per page (unchanged)
 */
export async function generateTableQRsPDF({
  tables,
  layout = "two-up",           // <-- default to the new two-up layout
  format = "a4",
  accentHex = "#83548C",
  showBorder = false,
  fileName = "table_qr_codes.pdf",
  caption = "Scan to order from this table.", // used under each QR (grid & two-up)
  showCaption = true,
}) {
  if (!Array.isArray(tables) || tables.length === 0) {
    throw new Error("No tables provided.");
  }

  const doc = new jsPDF({ unit: "mm", format });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const titleFont = () => { doc.setFont("helvetica", "bold"); doc.setTextColor(20,20,20); };
  const bodyFont  = () => { doc.setFont("helvetica", "normal"); doc.setTextColor(60,60,60); };

  // ---------------------------------------------------
  // LAYOUT: TWO-UP (2 per page, stacked)
  // ---------------------------------------------------
  if (layout === "two-up") {
    const margin = 14;
    const gutter = 8;
    const rows = 2;
    const cellW = pageW - margin * 2;
    const cellH = (pageH - margin * 2 - gutter * (rows - 1)) / rows;

    const drawCell = (x, y, w, h, t) => {
      if (showBorder) {
        doc.setDrawColor(accentHex);
        doc.setLineWidth(0.8);
        doc.roundedRect(x, y, w, h, 3, 3);
      }

      // Title
      titleFont();
      doc.setFontSize(16);
      doc.text(t.name || "Table", x + w / 2, y + 10, { align: "center" });

      // Inner padding and available space
      const pad = 8;
      const availableH = h - pad * 2 - 10 /*title*/ - (showCaption ? 10 : 0);

      // Large QR (maximize within cell)
      const qrSize = Math.min(w - pad * 2, availableH);
      const qrX = x + (w - qrSize) / 2;
      const qrY = y + 14 + (availableH - qrSize) / 2; // 14 ~ title area

      if (t.qrcodeString) {
        doc.addImage(t.qrcodeString, "PNG", qrX, qrY, qrSize, qrSize, undefined, "FAST");
      } else {
        doc.setDrawColor(200);
        doc.rect(qrX, qrY, qrSize, qrSize);
      }

      if (showCaption) {
        bodyFont();
        doc.setFontSize(11.5);
        doc.text(caption, x + w / 2, qrY + qrSize + 8, { align: "center", maxWidth: w - pad * 2 });
      }
    };

    let i = 0;
    while (i < tables.length) {
      if (i > 0) doc.addPage();

      // Top cell
      drawCell(margin, margin, cellW, cellH, tables[i]);
      i++;

      // Bottom cell
      if (i < tables.length) {
        drawCell(margin, margin + cellH + gutter, cellW, cellH, tables[i]);
        i++;
      }
    }

    doc.save(fileName);
    return;
  }

  // ---------------------------------------------------
  // LAYOUT: SINGLE (1 per page) — unchanged from before
  // ---------------------------------------------------
  if (layout === "single") {
    tables.forEach((t, idx) => {
      if (idx > 0) doc.addPage();

      const margin = 25;
      const boxX = margin;
      const boxY = margin;
      const boxW = pageW - margin * 2;
      const boxH = pageH - margin * 2;

      if (showBorder) {
        doc.setDrawColor(accentHex);
        doc.setLineWidth(1);
        doc.roundedRect(boxX, boxY, boxW, boxH, 3, 3);
      }

      titleFont();
      doc.setFontSize(40);
      doc.text(t.name || "Table", pageW / 2, boxY + 20, { align: "center" });

      const qrSize = Math.min(boxW, boxH) * 0.55;
      const qrX = (pageW - qrSize) / 2;
      const qrY = (pageH - qrSize) / 2 - 4;

      if (t.qrcodeString) {
        doc.addImage(t.qrcodeString, "PNG", qrX, qrY, qrSize, qrSize, undefined, "FAST");
      } else {
        doc.setDrawColor(200);
        doc.rect(qrX, qrY, qrSize, qrSize);
      }

      if (showCaption) {
        bodyFont();
        doc.setFontSize(12);
        doc.text(caption, pageW / 2, qrY + qrSize + 12, { align: "center" });
      }
    });

    doc.save(fileName);
    return;
  }

  // ---------------------------------------------------
  // LAYOUT: GRID (2x3 per page) — now with caption under each QR
  // ---------------------------------------------------
  const cols = 2;
  const rows = 3;
  const margin = 12;
  const gutter = 6;

  const cellW = (pageW - margin * 2 - gutter * (cols - 1)) / cols;
  const cellH = (pageH - margin * 2 - gutter * (rows - 1)) / rows;

  const drawCutMarks = (x, y, w, h) => {
    doc.setDrawColor(180);
    doc.setLineWidth(0.2);
    const len = 3.5;
    doc.line(x, y, x + len, y);
    doc.line(x, y, x, y + len);
    doc.line(x + w - len, y, x + w, y);
    doc.line(x + w, y, x + w, y + len);
    doc.line(x, y + h, x + len, y + h);
    doc.line(x, y + h - len, x, y + h);
    doc.line(x + w - len, y + h, x + w, y + h);
    doc.line(x + w, y + h - len, x + w, y + h);
  };

  let i = 0;
  while (i < tables.length) {
    if (i > 0) doc.addPage();

    for (let r = 0; r < rows && i < tables.length; r++) {
      for (let c = 0; c < cols && i < tables.length; c++, i++) {
        const t = tables[i];
        const x = margin + c * (cellW + gutter);
        const y = margin + r * (cellH + gutter);

        if (showBorder) {
          doc.setDrawColor(accentHex);
          doc.setLineWidth(0.8);
          doc.roundedRect(x, y, cellW, cellH, 2, 2);
        } else {
          drawCutMarks(x, y, cellW, cellH);
        }

        // Title
        titleFont();
        doc.setFontSize(14);
        doc.text(t.name || "Table", x + cellW / 2, y + 9, { align: "center" });

        // QR
        const pad = 7;
        const captionSpace = showCaption ? 9 : 0;
        const qrMaxW = cellW - pad * 2;
        const qrMaxH = cellH - pad * 2 - 16 /*title*/ - captionSpace;
        const qrSize = Math.min(qrMaxW, qrMaxH);
        const qrX = x + (cellW - qrSize) / 2;
        const qrY = y + (cellH - qrSize) / 2 + (showCaption ? -2 : 2); // small nudge

        if (t.qrcodeString) {
          doc.addImage(t.qrcodeString, "PNG", qrX, qrY, qrSize, qrSize, undefined, "FAST");
        } else {
          doc.setDrawColor(200);
          doc.rect(qrX, qrY, qrSize, qrSize);
        }

        // Caption under QR
        if (showCaption) {
          bodyFont();
          doc.setFontSize(10.5);
          doc.text(caption, x + cellW / 2, qrY + qrSize + 6, {
            align: "center",
            maxWidth: cellW - pad * 2,
          });
        }
      }
    }
  }

  doc.save(fileName);
}
